import { action, runInAction } from 'mobx';
import { createRequestString, getResponseBytes, http, isHttpError } from 'services/base/http';
import { IPangolinDomain, VPNServer } from './types';
import { ResultKind } from 'services/base/types';
import { VPNServerPangolinServerGetResponse, VPNServerPangolinServerObject, VPNServerPangolinServerSetRequest } from './protos/vpnserver_pb';


const internalUpdateVPNServer = action((domain: IPangolinDomain, server: VPNServer) => {
  domain.vpnServer.isEnabled = server.isEnabled;
  return domain.vpnServer;
});

export type VPNServerUpdateResult =
  | { kind: ResultKind.OK; server: VPNServer }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number }
  | { kind: ResultKind.USER_ERROR; status?: number };

export class VPNServerService {
  static async fetch(domain: IPangolinDomain): Promise<VPNServer> {
    const httpResp = await http.get(`/pangolin/${domain.id}/vpnserver`);
    return runInAction(() => {
      const response = VPNServerPangolinServerGetResponse.deserializeBinary(getResponseBytes(httpResp));
      const server = createVPNServerFromProto(response.getServer());
      internalUpdateVPNServer(domain, server);
      return server;
    });
  }

  static async update(domain: IPangolinDomain, server: VPNServer): Promise<VPNServerUpdateResult> {
    const url = `/pangolin/${domain.id}/vpnserver`;
    const request = createUpdateVPNServerRequest(server);
    try {
      await http.put(url, createRequestString(request));
      const result = internalUpdateVPNServer(domain, server);
      return { kind: ResultKind.OK, server: result };
    } catch (error) {
      const status = isHttpError(error) ? error.response?.status : undefined;
      const kind = status && status < 500 ? ResultKind.USER_ERROR : ResultKind.SYSTEM_ERROR;
      return { kind, status };
    }
  }
}

function createVPNServerFromProto(proto: VPNServerPangolinServerObject): VPNServer {
  return {
    isEnabled: proto.getIsEnabled() ?? false,
  };
}

function createUpdateVPNServerRequest(server: VPNServer): VPNServerPangolinServerSetRequest {
  const request = new VPNServerPangolinServerSetRequest();

  const proto = new VPNServerPangolinServerObject();
  proto.setIsEnabled(server.isEnabled);

  request.setServer(proto);

  return request;
}
